// Live preview of a festival's branding, on the account festival form.
//
// An organiser picks a colour, a logo and a banner, and until #220 saved them blind.
// Everything they picked appears somewhere they do not look: the festival page hero, and
// the "Part of X" badge on every event card in that festival's programme.
//
// The colour is the sharp end. The organiser used to pick the text colour too, and almost
// no freely-chosen pair passes WCAG AA — all three live festivals on 2026-09-20 failed, at
// 3.44:1, 4.18:1 and 3.38:1 against the 4.5:1 required, and every card carrying their
// badge inherited it. So since #416 the form asks for one colour and the text colour is
// derived from it: suggestTextColour() here, and Module::deriveFestivalTextColour() in
// modules/a11y, which is what actually writes the value on save.
//
// This module's whole job is that the preview shows the derived colour, not a guess, from
// the first keystroke rather than after a save.
//
// The WCAG maths below is deliberately duplicated from modules/a11y/services/Contrast.php.
// It has to run on every keystroke of a colour picker, and PHP cannot. The PHP service
// stays the source of truth — it is what renders the real page — and this must agree with
// it exactly, which is why the constants and the rule are copied rather than reinvented.
// If you change one, change both; contrast-test.php pins the PHP side.

// The bar a softened ink has to clear before it is preferred to pure black. Nothing on the
// site is held to AAA; this is a colour we chose ourselves, so it should not sit a decimal
// place above the minimum.
var AAA_NORMAL = 7.0;

// Pure black, not a near-black: #0b0c0c looks softer but the best of {#0b0c0c, white}
// against the worst background is 4.43:1, under AA. See the PHP service for the working.
var DARK = '#000000';
var LIGHT = '#ffffff';

// The site's own ink (Tailwind mine-shaft), so a badge on a pale background matches the
// body text beside it rather than going harder than it.
var SOFT_DARK = '#3d3d3d';

var DEFAULT_BG = '#fce8d0';

/** `#abc` and bare `abc` both become `#aabbcc`. Anything unusable becomes null. */
function normalise(colour) {
    if (colour === null || colour === undefined) {
        return null;
    }

    var value = String(colour).trim();
    if (value === '') {
        return null;
    }
    if (value.charAt(0) !== '#') {
        value = '#' + value;
    }

    var short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(value);
    if (short) {
        value = '#' + short[1] + short[1] + short[2] + short[2] + short[3] + short[3];
    }

    return /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
}

function toRgb(colour) {
    var hex = normalise(colour);
    if (hex === null) {
        return null;
    }

    return [
        parseInt(hex.substr(1, 2), 16),
        parseInt(hex.substr(3, 2), 16),
        parseInt(hex.substr(5, 2), 16),
    ];
}

/** Relative luminance, per WCAG. */
function luminance(rgb) {
    var channels = rgb.map(function (value) {
        var v = value / 255;
        return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** Contrast ratio, 1.0 (identical) to 21.0 (black on white). Null if either is unusable. */
function ratio(a, b) {
    var rgbA = toRgb(a);
    var rgbB = toRgb(b);
    if (rgbA === null || rgbB === null) {
        return null;
    }

    var lumA = luminance(rgbA);
    var lumB = luminance(rgbB);
    var hi = Math.max(lumA, lumB);
    var lo = Math.min(lumA, lumB);

    return (hi + 0.05) / (lo + 0.05);
}

/**
 * The text colour the site will give this background.
 *
 * Black or white by whichever contrasts better — a pair exhaustively verified to clear
 * 4.58:1 against any of the 16.7M sRGB backgrounds, so any colour a picker can produce
 * gets readable text. SOFT_DARK is preferred only where it clears AAA.
 *
 * Nothing already stored on the entry is consulted, which is what makes this agree with
 * the save: see Contrast::suggestTextColour().
 */
function suggestTextColour(background) {
    var bg = normalise(background);
    if (bg === null) {
        return DARK;
    }

    if ((ratio(LIGHT, bg) || 0) > (ratio(DARK, bg) || 0)) {
        return LIGHT;
    }

    return (ratio(SOFT_DARK, bg) || 0) >= AAA_NORMAL ? SOFT_DARK : DARK;
}

document.addEventListener('DOMContentLoaded', function () {

    var root = document.querySelector('[data-festival-preview]');

    // Same bundle on every account layout, so absent markup is the normal case.
    if (!root) {
        return;
    }

    var bgInput = document.getElementById('colourBg');
    var titleInput = document.getElementById('title');
    var logoInput = document.getElementById('festivalLogo');
    var bannerInput = document.getElementById('festivalImage');

    // Queried from the document rather than from `root`: the inputs these reflect are
    // spread across three sections of the form, and `root` is only the "is this the
    // festival form?" check.
    var hero = document.querySelector('[data-preview-hero]');
    var titleOut = document.querySelector('[data-preview-title]');
    var logoImg = document.querySelector('[data-preview-logo]');
    var logoPlaceholder = document.querySelector('[data-preview-logo-placeholder]');
    var bannerImg = document.querySelector('[data-preview-banner]');
    var bannerPlaceholder = document.querySelector('[data-preview-banner-placeholder]');

    // Object URLs are revoked before each replacement; a form with a few image swaps
    // otherwise leaks the lot until navigation.
    var objectUrls = [];

    function showPickedImage(input, img, placeholder) {
        if (!input || !img) {
            return;
        }

        var file = input.files && input.files[0];
        if (!file) {
            return; // Keep whatever is already saved and rendered.
        }

        var url = URL.createObjectURL(file);
        objectUrls.push(url);
        img.src = url;
        img.hidden = false;
        if (placeholder) {
            placeholder.hidden = true;
        }
    }

    function render() {
        var bg = normalise(bgInput && bgInput.value) || DEFAULT_BG;

        if (hero) {
            hero.style.background = bg;
            hero.style.color = suggestTextColour(bg);
        }

        if (titleOut) {
            titleOut.textContent = (titleInput && titleInput.value.trim()) || 'Your festival';
        }
    }

    ['input', 'change'].forEach(function (event) {
        if (bgInput) bgInput.addEventListener(event, render);
        if (titleInput) titleInput.addEventListener(event, render);
    });

    if (logoInput) {
        logoInput.addEventListener('change', function () {
            showPickedImage(logoInput, logoImg, logoPlaceholder);
        });
    }
    if (bannerInput) {
        bannerInput.addEventListener('change', function () {
            showPickedImage(bannerInput, bannerImg, bannerPlaceholder);
        });
    }

    window.addEventListener('pagehide', function () {
        objectUrls.forEach(function (url) {
            URL.revokeObjectURL(url);
        });
    });

    render();
});
